<template>
  <div class="px-6 py-6">
    <div class="flex items-center gap-4">
      <h2 class="text-lg font-semibold text-gray-900">Dashboard</h2>
      <select
        @change="handleProductChange($event)"
        class="text-sm border border-gray-300 rounded-md px-2 py-1"
      >
        <option value="">All Products</option>
        <option
          v-for="product in products"
          :key="product.id"
          :value="product.id"
        >
          {{ product.displayName }}
        </option>
      </select>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-2 gap-4 lg:grid-cols-4 pt-6" v-if="stats">
      <div class="bg-white rounded-lg border border-gray-200 px-4 py-3">
        <p class="text-sm text-gray-500">New</p>
        <p class="text-2xl font-bold text-gray-900">{{ stats.newCount }}</p>
      </div>
      <div class="bg-white rounded-lg border border-gray-200 px-4 py-3">
        <p class="text-sm text-gray-500">Bugs</p>
        <p class="text-2xl font-bold text-red-600">{{ stats.bugCount }}</p>
      </div>
      <div class="bg-white rounded-lg border border-gray-200 px-4 py-3">
        <p class="text-sm text-gray-500">Feature Requests</p>
        <p class="text-2xl font-bold text-indigo-600">{{ stats.featureRequestCount }}</p>
      </div>
      <div class="bg-white rounded-lg border border-gray-200 px-4 py-3">
        <p class="text-sm text-gray-500">Unassigned</p>
        <p class="text-2xl font-bold text-amber-600">{{ stats.unassignedCount }}</p>
      </div>
    </div>

    <div v-else class="pt-6 text-sm text-gray-500">Loading stats...</div>

    <!-- 7-Day Trend -->
    <div class="bg-white rounded-lg border border-gray-200 px-5 py-4 mt-6" v-if="trendData">
      <h3 class="text-sm font-medium text-gray-700 pb-2">7-Day Trend</h3>
      <TrendChart :data="trendData" />
    </div>

    <!-- Status Breakdown -->
    <div class="grid grid-cols-1 gap-6 pt-6 lg:grid-cols-2">
      <div class="bg-white rounded-lg border border-gray-200 px-5 py-4">
        <h3 class="text-sm font-medium text-gray-700">Status Breakdown</h3>
        <div class="flex flex-col gap-2 pt-3" v-if="stats">
          <StatusBar label="New" :count="stats.newCount" :total="stats.total" color="bg-blue-500" />
          <StatusBar label="Investigating" :count="stats.investigatingCount" :total="stats.total" color="bg-yellow-500" />
          <StatusBar label="Planned" :count="stats.plannedCount" :total="stats.total" color="bg-purple-500" />
          <StatusBar label="In Progress" :count="stats.inProgressCount" :total="stats.total" color="bg-indigo-500" />
          <StatusBar label="Resolved" :count="stats.resolvedCount" :total="stats.total" color="bg-green-500" />
        </div>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 px-5 py-4">
        <h3 class="text-sm font-medium text-gray-700">Type Breakdown</h3>
        <div class="flex flex-col gap-2 pt-3" v-if="stats">
          <StatusBar label="Bugs" :count="stats.bugCount" :total="stats.total" color="bg-red-500" />
          <StatusBar label="Features" :count="stats.featureRequestCount" :total="stats.total" color="bg-indigo-500" />
          <StatusBar label="Improvements" :count="stats.improvementCount" :total="stats.total" color="bg-teal-500" />
          <StatusBar label="Support" :count="stats.supportCount" :total="stats.total" color="bg-amber-500" />
          <StatusBar label="Praise" :count="stats.praiseCount" :total="stats.total" color="bg-green-500" />
          <StatusBar label="Confusion" :count="stats.confusionCount" :total="stats.total" color="bg-orange-500" />
          <StatusBar label="Uncategorized" :count="stats.uncategorizedCount" :total="stats.total" color="bg-gray-400" />
        </div>
      </div>
    </div>

    <!-- Top Priority -->
    <div class="bg-white rounded-lg border border-gray-200 px-5 py-4 mt-6">
      <div class="flex items-center gap-3">
        <h3 class="text-sm font-medium text-gray-700">Top Priority Items</h3>
        <button
          @click="uiStore.setCurrentPage('feedback')"
          class="text-xs text-indigo-600 hover:text-indigo-800"
        >
          View all
        </button>
      </div>
      <div class="flex flex-col gap-2 pt-3" v-if="topPriority && topPriority.length > 0">
        <div
          v-for="item in topPriority"
          :key="item.id"
          @click="uiStore.viewFeedbackDetail(item.id)"
          class="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-50 cursor-pointer"
        >
          <span
            class="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold text-white"
            :class="getPriorityColor(item.priorityScore)"
          >
            {{ item.priorityScore }}
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-sm text-gray-900 truncate">{{ item.aiSummary || item.transcript }}</p>
            <p class="text-xs text-gray-500">{{ item.type || 'uncategorized' }} · {{ formatDate(item.createdAt) }}</p>
          </div>
          <span
            class="text-xs px-2 py-0.5 rounded-full"
            :class="getStatusClass(item.status)"
          >
            {{ item.status }}
          </span>
        </div>
      </div>
      <p v-else class="text-sm text-gray-400 pt-2">No feedback items yet</p>
    </div>

    <!-- Product Breakdown & LLM Stats -->
    <div class="grid grid-cols-1 gap-6 mt-6 lg:grid-cols-2">
      <div class="bg-white rounded-lg border border-gray-200 px-5 py-4" v-if="productBreakdown && productBreakdown.length > 0">
        <h3 class="text-sm font-medium text-gray-700">By Product</h3>
        <div class="flex flex-col gap-2 pt-3">
          <div
            v-for="pb in productBreakdown"
            :key="pb.productId"
            class="flex items-center gap-3 text-sm"
          >
            <span class="font-medium text-gray-700 w-32">{{ pb.productName }}</span>
            <span class="text-gray-500">{{ pb.total }} total</span>
            <span class="text-blue-600">{{ pb.openCount }} open</span>
            <span class="text-green-600">{{ pb.newCount }} new</span>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 px-5 py-4" v-if="llmStats">
        <h3 class="text-sm font-medium text-gray-700">AI Usage (24h)</h3>
        <div class="grid grid-cols-2 gap-x-6 gap-y-2 pt-3 text-sm">
          <div>
            <span class="text-gray-500">Calls:</span>
            <span class="font-medium text-gray-800"> {{ llmStats.totalCalls }}</span>
          </div>
          <div>
            <span class="text-gray-500">Success:</span>
            <span class="font-medium text-green-600"> {{ llmStats.successful }}/{{ llmStats.totalCalls }}</span>
          </div>
          <div>
            <span class="text-gray-500">Avg Latency:</span>
            <span class="font-medium text-gray-800"> {{ llmStats.avgLatencyMs }}ms</span>
          </div>
          <div>
            <span class="text-gray-500">Cost:</span>
            <span class="font-medium text-gray-800"> ${{ llmStats.totalCost.toFixed(4) }}</span>
          </div>
        </div>
        <div class="flex flex-col gap-1 pt-3" v-if="Object.keys(llmStats.byProvider).length > 0">
          <p class="text-xs text-gray-500">By provider:</p>
          <div
            v-for="(info, provider) in llmStats.byProvider"
            :key="provider"
            class="flex items-center gap-2 text-xs"
          >
            <span class="text-gray-600 w-28">{{ provider }}</span>
            <span class="text-gray-800">{{ info.calls }} calls</span>
            <span class="text-gray-500">${{ info.cost.toFixed(4) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import apiClient from '../../api/client'
import { useUIStore } from '../../stores/uiStore'
import StatusBar from '../../components/admin/StatusBar.vue'
import TrendChart from '../../components/admin/TrendChart.vue'

const uiStore = useUIStore()

const stats = ref<any>(null)
const topPriority = ref<any[]>([])
const products = ref<any[]>([])
const trendData = ref<any>(null)
const productBreakdown = ref<any[]>([])
const llmStats = ref<any>(null)

async function fetchProductFiltered() {
  const pid = uiStore.selectedProductId ?? undefined
  const [statsData, topData, trendsData] = await Promise.all([
    apiClient.admin.getStats(pid),
    apiClient.admin.getTopPriority(pid, 5),
    apiClient.admin.getTrends(pid),
  ])
  stats.value = statsData
  topPriority.value = topData
  trendData.value = trendsData
}

async function fetchGlobal() {
  const [productsData, breakdownData, llmData] = await Promise.all([
    apiClient.products.list(),
    apiClient.admin.getProductBreakdown(),
    apiClient.llm.getUsageStats(),
  ])
  products.value = productsData
  productBreakdown.value = breakdownData
  llmStats.value = llmData
}

onMounted(() => {
  fetchProductFiltered()
  fetchGlobal()
})

watch(() => uiStore.selectedProductId, () => {
  fetchProductFiltered()
})

function handleProductChange(event: Event) {
  const value = (event.target as HTMLSelectElement).value
  uiStore.setSelectedProduct(value ? Number(value) : null)
}

function getPriorityColor(score: number): string {
  if (score >= 80) return 'bg-red-600'
  if (score >= 60) return 'bg-orange-500'
  if (score >= 40) return 'bg-yellow-500'
  if (score >= 20) return 'bg-blue-500'
  return 'bg-gray-400'
}

function getStatusClass(status: string): string {
  const classes: Record<string, string> = {
    new: 'bg-blue-100 text-blue-700',
    investigating: 'bg-yellow-100 text-yellow-700',
    planned: 'bg-purple-100 text-purple-700',
    in_progress: 'bg-indigo-100 text-indigo-700',
    resolved: 'bg-green-100 text-green-700',
    archived: 'bg-gray-100 text-gray-600',
    wont_fix: 'bg-gray-100 text-gray-600',
  }
  return classes[status] || 'bg-gray-100 text-gray-600'
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const hours = Math.floor(diff / 3600000)
  if (hours < 1) return 'just now'
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}
</script>
